/**
 * Tutorial Context - Manages tutorial state globally
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import tutorialService from '@/services/tutorialService';
import type { OnboardingState, HighlightState } from '@/types/tutorial';

interface TutorialContextType {
  onboardingState: OnboardingState | null;
  isHighlighting: boolean;
  highlightState: HighlightState | null;
  currentTutorialId: string | null;
  currentStepIndex: number;
  
  // Methods
  startTutorial: (tutorialId: string) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTutorial: () => void;
  completeTutorial: () => void;
  dismissBlueDot: (dotId: string) => void;
  startHighlight: (state: HighlightState) => void;
  stopHighlight: () => void;
  resetOnboarding: () => void;
  updatePreferences: (preferences: any) => void;
  isFirstTimeUser: () => boolean;
  isTutorialCompleted: (tutorialId: string) => boolean;
  setFirstTimeUserComplete: () => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const TutorialProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [highlightState, setHighlightState] = useState<HighlightState | null>(null);
  const [currentTutorialId, setCurrentTutorialId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Initialize onboarding state on mount
  useEffect(() => {
    const state = tutorialService.getOnboardingState();
    if (!state) {
      setOnboardingState(tutorialService.initializeOnboarding());
    } else {
      setOnboardingState(state);
    }
  }, []);

  const startTutorial = (tutorialId: string) => {
    setCurrentTutorialId(tutorialId);
    setCurrentStepIndex(0);
    const progress = tutorialService.getTutorialProgress(tutorialId);
    if (progress) {
      setCurrentStepIndex(progress.currentStepIndex);
    }
  };

  const nextStep = () => {
    setCurrentStepIndex((prev) => prev + 1);
  };

  const previousStep = () => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  };

  const skipTutorial = () => {
    if (currentTutorialId) {
      setCurrentTutorialId(null);
      setCurrentStepIndex(0);
    }
  };

  const completeTutorial = () => {
    if (currentTutorialId) {
      tutorialService.completeTutorial(currentTutorialId);
      setOnboardingState(tutorialService.getOnboardingState());
      setCurrentTutorialId(null);
      setCurrentStepIndex(0);
    }
  };

  const dismissBlueDot = (dotId: string) => {
    tutorialService.dismissBlueDot(dotId);
    setOnboardingState(tutorialService.getOnboardingState());
  };

  const startHighlight = (state: HighlightState) => {
    setHighlightState(state);
    setIsHighlighting(true);
  };

  const stopHighlight = () => {
    setHighlightState(null);
    setIsHighlighting(false);
  };

  const resetOnboarding = () => {
    tutorialService.resetOnboarding();
    setOnboardingState(tutorialService.initializeOnboarding());
    setCurrentTutorialId(null);
    setCurrentStepIndex(0);
  };

  const updatePreferences = (preferences: any) => {
    tutorialService.updatePreferences(preferences);
    setOnboardingState(tutorialService.getOnboardingState());
  };

  const isFirstTimeUser = () => {
    return onboardingState?.firstTimeUser ?? false;
  };

  const isTutorialCompleted = (tutorialId: string) => {
    return tutorialService.isTutorialCompleted(tutorialId);
  };

  const value: TutorialContextType = {
    onboardingState,
    isHighlighting,
    highlightState,
    currentTutorialId,
    currentStepIndex,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    dismissBlueDot,
    startHighlight,
    stopHighlight,
    resetOnboarding,
    updatePreferences,
    isFirstTimeUser,
    isTutorialCompleted,
    setFirstTimeUserComplete: tutorialService.setFirstTimeUserComplete.bind(tutorialService),
  };

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};
